"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared";
import { MediaApi } from "@/openapi/generated";
import type { EntryType } from "@/openapi/generated/models";
import { MediaGroup } from "@/openapi/generated/models";
import { createAuthenticatedApi } from "@/lib/utils";
import { invalidateAllTeacherMediaQueries } from "@/lib/utils/media/invalidate-teacher-media-queries";
import {
  mediaListContainsId,
  parseMediaIdFromUnknown,
} from "@/lib/utils/media/fetch-media-asset";
import { invalidateMediaAssetQueries } from "@/lib/utils/media/media-asset-query-key";
import {
  compressImageForUpload,
  getImageUploadPresetFromMediaGroup,
} from "@/features/media/utils/compressImageForUpload";
import type { MediaOwnerType } from "./useMediaAssetGet";

export const useMediaAssetUpload = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (request: {
      tenantId: number;
      ownerType: MediaOwnerType;
      ownerId: number;
      mediaGroup: MediaGroup;
      file: File;
      previousMediaId?: number | null;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(MediaApi, accessToken);

      const uploadPreset = getImageUploadPresetFromMediaGroup(request.mediaGroup);
      let fileToUpload = request.file;
      if (uploadPreset) {
        try {
          fileToUpload = await compressImageForUpload(request.file, uploadPreset);
        } catch {
          fileToUpload = request.file;
        }
      }

      // Backend expects EntryType enum (e.g. "Tenant", "User"). MediaOwnerType matches.
      const uploadResult = await service.apiMediaUploadPost({
        file: fileToUpload,
        entryId: request.ownerId.toString(),
        entryType: request.ownerType as EntryType,
        mediaGroup: request.mediaGroup,
        xTenantID: Number(request.tenantId),
      });

      if (!uploadResult.isSuccess || !uploadResult.data) {
        throw new Error("Upload failed");
      }

      const newMediaId = parseMediaIdFromUnknown(uploadResult.data.mediaId);

      // Teacher profile uploads overwrite per-teacher storage (e.g. …/Teacher/{id}/profile.png).
      // Never delete a previous row here - mediaId is tenant-global and stale ids from another
      // teacher would remove the wrong asset.
      const isTeacherProfileUpload =
        request.ownerType === "Teacher" &&
        request.mediaGroup === MediaGroup.Profile;

      // Backend reuses the same mediaId when overwriting (e.g. profile.png / cover.png).
      // Only delete previous media when it belongs to this owner - stale mediaId from
      // another teacher/entity must not be removed (global media id pool).
      if (
        !isTeacherProfileUpload &&
        newMediaId > 0 &&
        request.previousMediaId &&
        request.previousMediaId > 0 &&
        request.previousMediaId !== newMediaId
      ) {
        try {
          const mediaList = await service.apiMediaTypeIdGet({
            type: request.ownerType as never,
            id: request.ownerId,
            xTenantID: Number(request.tenantId),
          });

          if (mediaListContainsId(mediaList, request.previousMediaId)) {
            await service.apiMediaIdDelete({
              id: request.previousMediaId,
              xTenantID: Number(request.tenantId),
            });
          }
        } catch {
          // ignore
        }
      }

      return uploadResult;
    },
    onSuccess: async (_, variables) => {
      if (variables.ownerType === "Teacher") {
        await invalidateAllTeacherMediaQueries(queryClient, variables.tenantId);
      } else {
        await invalidateMediaAssetQueries(
          queryClient,
          variables.tenantId,
          variables.ownerType,
          variables.ownerId,
          variables.mediaGroup,
        );
      }

      toast({
        title: "File uploaded",
        description: "Upload completed successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to upload file";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutateAsync, isPending, isError, isSuccess };
};

